import { Controller } from "@hotwired/stimulus";

/**
 * image-fallback (Stimulus controller)
 *
 * But:
 * - remplacer le `src` d'une `<img>` par une image de fallback lorsque le chargement échoue
 *   (404, hostname injoignable, image corrompue, etc.).
 *
 * Usage:
 *   <img
 *     data-controller="image-fallback"
 *     data-image-fallback-default-src-value="<%= asset_path('default-listing.svg') %>"
 *     src="..." />
 *
 * Comportement:
 * - Au connect(), on attache un listener `error` sur l'élément <img>.
 * - Au connect(), on vérifie aussi si l'image a déjà échoué avant que Stimulus ait
 *   connecté le controller (race possible avec `remote-bind` qui set le `src` avant
 *   l'insertion DOM). On utilise `HTMLImageElement.decode()` qui rejette si l'image
 *   n'a pas pu être décodée. Cette approche évite les faux positifs de la heuristique
 *   `naturalWidth === 0` (qui matche aussi les SVG valides sans dimensions intrinsèques).
 * - Sur `error`, on remplace `src` par `defaultSrcValue`.
 * - Garde anti-boucle infinie: si le `src` courant est déjà la valeur de fallback,
 *   on ne fait rien (au cas où le fallback lui-même serait inaccessible).
 * - Compatible avec un re-rendu dynamique (ex: `remote-bind` qui réécrit `src`):
 *   le listener n'est pas retiré après un fallback; la garde anti-boucle empêche le loop.
 * - Au disconnect(), le listener est retiré.
 */
export default class extends Controller {
  static values = {
    defaultSrc: String,
  };

  connect() {
    this.onError = () => this.applyFallback();
    this.element.addEventListener("error", this.onError);

    if (this.element.getAttribute("src") && typeof this.element.decode === "function") {
      this.element.decode().catch(() => this.applyFallback());
    }
  }

  disconnect() {
    try { this.element.removeEventListener("error", this.onError); } catch (_) {}
  }

  applyFallback() {
    const fallback = this.hasDefaultSrcValue ? this.defaultSrcValue : "";
    if (!fallback) return;
    if (this.isShowingFallback()) return;
    try { this.element.setAttribute("src", fallback); } catch (_) {}
  }

  isShowingFallback() {
    const fallback = this.hasDefaultSrcValue ? this.defaultSrcValue : "";
    if (!fallback) return false;
    const current = this.element.getAttribute("src") || "";
    if (current === fallback) return true;
    try {
      return new URL(current, window.location.href).href === new URL(fallback, window.location.href).href;
    } catch (_) {
      return false;
    }
  }
}
